import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, Check, AlertTriangle, FolderTree, ChevronRight, Plus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { toast } from 'sonner';

/**
 * İndirilen dosyanın adını sunucunun `Content-Disposition` başlığından okur.
 *
 * Blob ile indirirken `a.download` başlığı EZER; ad burada sabit yazılıydı ve
 * ürün, reçete, stok şablonlarının üçü de "sablon.xlsx" olarak iniyordu
 * (kullanıcı, 2026-09-27). Ad artık tek yerde — sunucuda — tanımlı.
 *
 * `filename*` ÖNCE denenir: Türkçe harfler yalnızca orada doğru taşınıyor,
 * düz `filename` ASCII'ye indirgenmiş yedektir (RFC 6266).
 */
export const sunucununDosyaAdi = (disposition, varsayilan = 'sablon.xlsx') => {
  if (!disposition) return varsayilan;
  const yildizli = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (yildizli) {
    try { return decodeURIComponent(yildizli[1].trim()); } catch { /* bozuk kodlama */ }
  }
  const duz = /filename="?([^";]+)"?/i.exec(disposition);
  return (duz && duz[1].trim()) || varsayilan;
};

export default function ImportDialog({ open, onOpenChange, config }) {
  const { title, templateUrl, previewUrl, executeUrl, approveCategoriesUrl, columns, onComplete, isProductImport, templateDownloadFn, isUpdateMode } = config;
  const [step, setStep] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const fileRef = useRef(null);

  const reset = () => { setStep('upload'); setPreview(null); setResult(null); };
  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  // Load units for dropdown
  useEffect(() => {
    if (open && isProductImport) {
      api.get('/units').then(r => setUnits(r.data)).catch(() => {});
    }
  }, [open, isProductImport]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(previewUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      // If product import and has new categories, show category approval first
      if (isProductImport && res.data.has_new_categories) {
        setStep('categories');
      } else {
        setStep('preview');
      }
    } catch (err) {
      toast.error('Dosya islenemedi: ' + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleApproveCategories = async () => {
    if (!approveCategoriesUrl || !preview?.category_tree) return;
    setLoading(true);
    try {
      const res = await api.post(approveCategoriesUrl, { categories: preview.category_tree });
      toast.success(`${res.data.created} yeni kategori olusturuldu`);
      setStep('preview');
    } catch (err) {
      toast.error('Kategori olusturma hatasi');
    }
    setLoading(false);
  };

  const updateRow = (origIdx, field, value) => {
    if (!preview) return;
    const key = preview.rows ? 'rows' : 'recipes';
    const items = [...(preview[key] || [])];
    if (origIdx < 0 || origIdx >= items.length) return;
    items[origIdx] = { ...items[origIdx], [field]: value };
    if (field === 'unit') {
      items[origIdx].unit_error = null;
      items[origIdx].errors = (items[origIdx].errors || []).filter(e => !e.includes('birim') && !e.includes('Gecersiz birim'));
      items[origIdx].valid = (items[origIdx].errors || []).length === 0;
    }
    const valid = items.filter(i => i.valid).length;
    setPreview({ ...preview, [key]: items, valid, invalid: items.length - valid });
  };

  const handleExecute = async () => {
    setLoading(true);
    try {
      let payload;
      if (isUpdateMode) {
        payload = { rows: (preview.rows || []).filter(r => r.valid && r.has_changes) };
      } else {
        payload = preview.rows
          ? { rows: preview.rows.filter(r => r.valid) }
          : { recipes: (preview.recipes || []).filter(r => r.valid) };
      }
      const res = await api.post(executeUrl, payload);
      setResult(res.data);
      setStep('result');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error('Import hatasi: ' + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  const skipInvalid = () => {
    if (!preview) return;
    const key = preview.rows ? 'rows' : 'recipes';
    const items = (preview[key] || []).filter(i => i.valid);
    setPreview({ ...preview, [key]: items, total: items.length, valid: items.length, invalid: 0 });
    toast.success('Hatali satirlar atildi');
  };

  const items = preview?.rows || preview?.recipes || [];
  const validCount = preview?.valid || 0;
  const invalidCount = preview?.invalid || 0;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-[#3d6e4e]" />
            {title}
            {/* Step indicator for product import */}
            {isProductImport && step !== 'upload' && step !== 'result' && (
              <div className="flex items-center gap-1 ml-4 text-xs font-normal">
                <span className={step === 'categories' ? 'text-[#3d6e4e] font-semibold' : 'text-[#8a7e70]'}>Kategoriler</span>
                <ChevronRight size={12} className="text-[#d0c4b0]" />
                <span className={step === 'preview' ? 'text-[#3d6e4e] font-semibold' : 'text-[#8a7e70]'}>Urunler</span>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="py-6 space-y-6">
              <div className="flex items-center gap-4 p-4 bg-[#e8dfd0]/50 rounded-lg border border-[#d0c4b0]">
                <Download size={20} className="text-[#3F3E39] flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1a1916]">
                    {isUpdateMode ? 'Mevcut urunleri indirin, degistirin, tekrar yukleyin' : 'Sablon dosyasini indirin'}
                  </p>
                  <p className="text-xs text-[#8a7e70]">
                    {isUpdateMode ? 'Excel dosyasinda degisiklikleri yapin ve yukleyin' : 'Verileri bu sablona gore doldurun'}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="border-[#d0c4b0] text-[#3F3E39]" data-testid="download-template-btn"
                  onClick={async () => {
                    if (templateDownloadFn) { await templateDownloadFn(); return; }
                    try {
                      const tplPath = templateUrl.startsWith('/api/erp') ? templateUrl.replace('/api/erp', '') : templateUrl;
                      const res = await api.get(tplPath, { responseType: 'blob' });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement('a'); a.href = url;
                      a.download = sunucununDosyaAdi(res.headers?.['content-disposition']);
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { window.open(`${BACKEND_URL}${templateUrl}`, '_blank'); }
                  }}>
                  <Download size={14} className="mr-1.5" /> {isUpdateMode ? 'Mevcut Urunleri Indir' : 'Sablon Indir'}
                </Button>
              </div>
              <div className="border-2 border-dashed border-[#d0c4b0] rounded-lg p-8 text-center cursor-pointer hover:border-[#8a7e70] transition-colors"
                onClick={() => fileRef.current?.click()} data-testid="file-upload-area">
                <Upload size={32} className="mx-auto text-[#8a7e70] mb-3" />
                <p className="text-sm font-medium text-[#1a1916]">Dosya yuklemek icin tiklayin</p>
                <p className="text-xs text-[#8a7e70] mt-1">.xlsx formati</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} className="hidden" data-testid="file-input" />
              </div>
              {loading && <p className="text-center text-sm text-[#8a7e70] animate-pulse">Dosya isleniyor...</p>}
            </div>
          )}

          {/* Step 2: Category Approval (product import only) */}
          {step === 'categories' && preview?.category_tree && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-2 p-3 bg-[#3d6e4e]/8 border border-[#3d6e4e]/20 rounded-lg">
                <FolderTree size={18} className="text-[#3d6e4e] flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-[#1a1916]">Yeni kategoriler tespit edildi</p>
                  <p className="text-xs text-[#8a7e70]">Asagidaki kategoriler sistemde yok, olusturulacak. Onaylayin.</p>
                </div>
              </div>

              <div className="border border-[#d0c4b0] rounded-lg bg-white p-4 space-y-3">
                {preview.category_tree.map((cat, i) => (
                  <div key={i}>
                    <div className="flex items-center gap-2 py-1.5">
                      {cat.is_new ? (
                        <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e] text-xs"><Plus size={10} className="mr-0.5" /> Yeni</span>
                      ) : (
                        <span className="status-badge bg-[#e8dfd0] text-[#8a7e70] text-xs">Mevcut</span>
                      )}
                      <span className="font-semibold text-[#1a1916]">{cat.name}</span>
                    </div>
                    {cat.subcategories?.map((sub, j) => (
                      <div key={j} className="flex items-center gap-2 py-1 pl-8">
                        <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e] text-xs"><Plus size={10} className="mr-0.5" /> Yeni</span>
                        <span className="text-sm text-[#3F3E39]">{sub.name}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <StatBox label="Toplam Urun" value={preview.total} />
                <StatBox label="Gecerli" value={preview.valid} color="#3d6e4e" />
                <StatBox label="Hatali" value={preview.invalid} color="#7a3535" />
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {isUpdateMode ? (
                  <>
                    <StatBox label="Toplam" value={items.length} />
                    <StatBox label="Degisen" value={preview.changed || items.filter(i => i.has_changes).length} color="#7a6a2e" />
                    <StatBox label="Ayni" value={preview.unchanged || items.filter(i => i.valid && !i.has_changes).length} color="#8a7e70" />
                    <StatBox label="Hatali" value={invalidCount} color="#7a3535" />
                  </>
                ) : (
                  <>
                    <StatBox label="Toplam" value={items.length} />
                    <StatBox label="Gecerli" value={validCount} color="#3d6e4e" />
                    <StatBox label="Hatali" value={invalidCount} color="#7a3535" />
                  </>
                )}
              </div>

              {/* Error rows first */}
              {invalidCount > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-[#7a3535] mb-2 flex items-center gap-1.5">
                    <AlertTriangle size={14} /> Hatali Satirlar ({invalidCount})
                  </h3>
                  <div className="border border-[#7a3535]/30 rounded-lg overflow-hidden mb-4">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#7a3535]/10 hover:bg-[#7a3535]/10">
                          <TableHead className="text-[#7a3535] font-bold text-xs uppercase w-10">#</TableHead>
                          {columns.map(col => (
                            <TableHead key={col.key} className="text-[#7a3535] font-bold text-xs uppercase">{col.label}</TableHead>
                          ))}
                          <TableHead className="text-[#7a3535] font-bold text-xs uppercase">Hata</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.filter(it => !it.valid).map((item) => {
                          const origIdx = items.indexOf(item);
                          return (
                            <TableRow key={`err-${origIdx}`} className="border-[#d0c4b0] bg-[#7a3535]/5">
                              <TableCell className="text-xs text-[#8a7e70]">{item.row || '?'}</TableCell>
                              {columns.map(col => (
                                <TableCell key={col.key} className="text-sm text-[#3F3E39]">
                                  {col.key === 'unit' && col.editable && units.length > 0 ? (
                                    <Select value={item.unit || undefined} onValueChange={(v) => updateRow(origIdx, 'unit', v)}>
                                      <SelectTrigger className="h-7 text-xs bg-white border-[#7a3535]/50 w-24">
                                        <SelectValue placeholder={item.unit || 'Birim sec'} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {units.map(u => <SelectItem key={u.id} value={u.abbreviation}>{u.abbreviation} - {u.name}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  ) : col.editable ? (
                                    <Input value={item[col.key] ?? ''} onChange={(e) => updateRow(origIdx, col.key, e.target.value)}
                                      className="h-7 text-xs bg-white border-[#d0c4b0]" />
                                  ) : (col.render ? col.render(item) : (item[col.key] ?? '-'))}
                                </TableCell>
                              ))}
                              <TableCell className="text-xs text-[#7a3535]">{item.errors?.join(', ')}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Valid rows */}
              <div>
                <h3 className="text-sm font-bold text-[#3d6e4e] mb-2 flex items-center gap-1.5">
                  <Check size={14} /> Gecerli Satirlar ({validCount})
                </h3>
                <div className="border border-[#d0c4b0] rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0] sticky top-0 z-10">
                        <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-10">#</TableHead>
                        {columns.map(col => (
                          <TableHead key={col.key} className="text-[#1a1916] font-bold text-xs uppercase">{col.label}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.filter(it => it.valid).map((item, i) => (
                        <TableRow key={`ok-${i}`} className="border-[#d0c4b0]">
                          <TableCell className="text-xs text-[#8a7e70]">{item.row || i + 1}</TableCell>
                          {columns.map(col => (
                            <TableCell key={col.key} className="text-xs text-[#3F3E39]">
                              {col.render ? col.render(item) : (item[col.key] ?? '-')}
                              {col.key === 'category' && item.cat_is_new && <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e] text-xs ml-1">Yeni</span>}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 'result' && result && (
            <div className="py-6 space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-[#3d6e4e]/10 flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-[#3d6e4e]" />
                </div>
                <h3 className="text-xl font-bold text-[#1a1916]">{isUpdateMode ? 'Guncelleme Tamamlandi' : 'Import Tamamlandi'}</h3>
              </div>
              <div className="flex gap-4 justify-center">
                {isUpdateMode ? (
                  <>
                    <StatBox label="Guncellendi" value={result.updated || result.added} color="#3d6e4e" />
                    <StatBox label="Atlandi" value={result.skipped} color="#7a6a2e" />
                  </>
                ) : (
                  <>
                    <StatBox label="Eklendi" value={result.added} color="#3d6e4e" />
                    <StatBox label="Atlandi" value={result.skipped} color="#7a6a2e" />
                    <StatBox label="Hata" value={result.errors} color="#7a3535" />
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-[#d0c4b0] pt-4">
          {step === 'categories' && (
            <div className="flex w-full justify-between">
              <Button variant="outline" onClick={reset} className="border-[#d0c4b0]">Geri</Button>
              <Button onClick={handleApproveCategories} disabled={loading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="approve-categories-btn">
                {loading ? 'Olusturuluyor...' : 'Kategorileri Onayla ve Devam Et'}
              </Button>
            </div>
          )}
          {step === 'preview' && (
            <div className="flex w-full justify-between">
              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} className="border-[#d0c4b0]">Geri</Button>
                {invalidCount > 0 && (
                  <Button variant="outline" onClick={skipInvalid} className="border-[#7a6a2e] text-[#7a6a2e] hover:bg-[#7a6a2e]/10">
                    Hatali Satirlari Atla ({invalidCount})
                  </Button>
                )}
              </div>
              <Button onClick={handleExecute} disabled={loading || (isUpdateMode ? (preview?.changed || 0) === 0 : validCount === 0)}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="import-execute-btn">
                {loading ? 'Yukleniyor...' : isUpdateMode ? `${preview?.changed || 0} Urunu Guncelle` : `${validCount} Gecerli Kaydi Yukle`}
              </Button>
            </div>
          )}
          {step === 'result' && (
            <Button onClick={() => handleClose(false)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kapat</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatBox({ label, value, color = '#2e2d29' }) {
  return (
    <div className="flex-1 p-3 rounded-lg border border-[#d0c4b0] bg-white text-center">
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      <p className="text-xs text-[#8a7e70] font-medium">{label}</p>
    </div>
  );
}
